/* eslint-disable import/prefer-default-export */

import chatterSaga from './chatterSaga';
import callingSaga from './callingSaga';
import notificationSaga from './notificationSaga';
import tokenManager from '../services/tokenManager';
import authService from '../services/authService';
import { markInitializationStarted, markInitializationComplete, startTokenRefresh } from '../config/axiosConfig';

const { take, put, call, spawn } = require('redux-saga/effects');
const DetectRTC = require('detectrtc');

// Check if user data was server-rendered (for backward compatibility during migration)
const hasServerUserData = !!(typeof global.userData !== 'undefined' && global.userData.username);

let hasInitialized = false; // Prevent multiple initializations

const loadImpersonationFromStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  const token = window.sessionStorage.getItem('impersonationAccessToken');
  const userRaw = window.sessionStorage.getItem('impersonationUser');

  if (!token || !userRaw) {
    return null;
  }

  try {
    const user = JSON.parse(userRaw);
    return { token, user };
  } catch (e) {
    return null;
  }
};

export const clearImpersonationStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  window.sessionStorage.removeItem('impersonationAccessToken');
  window.sessionStorage.removeItem('impersonationUser');
};

function* performInitialization() {
  // console.log('[App Init] Starting initialization');

  // Mark initialization as started so request interceptor knows to wait
  markInitializationStarted();

  // Helper to expose impersonation status for navigation/UI
  const setImpersonationFlag = () => {
    if (typeof window !== 'undefined') {
      window.isImpersonating = tokenManager.isImpersonated();
    }
  };

  // Option 0: Stored impersonation session (token kept in sessionStorage)
  const storedImpersonation = loadImpersonationFromStorage();
  if (storedImpersonation?.token && storedImpersonation?.user) {
    tokenManager.setTokens(storedImpersonation.token);
    const isValid = tokenManager.isAuthenticated() && tokenManager.isImpersonated();

    if (!isValid) {
      clearImpersonationStorage();
      setImpersonationFlag(); // will set false
    } else {
      setImpersonationFlag();
      markInitializationComplete();
      yield put({
        type: 'FETCH_CREDENTIALS_FULFILLED',
        payload: { data: storedImpersonation.user },
      });
      return;
    }
  }

  yield spawn(chatterSaga);
  yield spawn(callingSaga);
  yield spawn(notificationSaga);

  yield put({
    type: 'WHITE_LABEL_SETTINGS_FULFILLED',
    payload: {
      data: globalThis.whiteLabelSettings,
    },
  });

  yield call(DetectRTC.isLoaded);

  // Try to restore user session
  // Option 1: Server-rendered user data (backward compatibility)
  if (hasServerUserData) {
    // Legacy mode: Use server-rendered data
    // This allows gradual migration - backend still sets initial tokens
    if (global.userData.accessToken) {
      // Store access token (refresh token is in httpOnly cookie)
      tokenManager.setTokens(global.userData.accessToken);
      setImpersonationFlag();
    }

    // Mark initialization as complete
    markInitializationComplete();

    yield put({
      type: 'FETCH_CREDENTIALS_FULFILLED',
      payload: { data: global.userData },
    });
  } else {
    // Option 2: Try to get tokens using refresh token endpoint
    // Dispatch pending action so UI knows credentials are being fetched
    yield put({
      type: 'FETCH_CREDENTIALS_PENDING',
    });

    let refreshSucceeded = false;
    try {
      // Use refresh token endpoint to get new access token
      // console.log('[App Init] Attempting to refresh token');

      // Call startTokenRefresh - it will throw an error if refresh fails (401 or timeout)
      // The request interceptor has a 3-second timeout that will reject the promise
      const refreshResult = yield call(startTokenRefresh, null);

      // console.log('[App Init] Successfully got tokens from refresh token');
      // console.log('[App Init] User from refresh token:', refreshResult?.user?.username || refreshResult?.user?.id);

      // If refreshResult is null or undefined, treat as failure
      if (!refreshResult) {
        throw new Error('Refresh token returned no result');
      }

      if (refreshResult && refreshResult.accessToken && refreshResult.user) {
        const { accessToken, user } = refreshResult;

        // Log user data structure for debugging
        console.log('[App Init] Refresh successful, user data:', {
          hasUser: !!user,
          hasProfile: !!(user && user.profile),
          hasFirstName: !!(user && user.profile && user.profile.first_name),
          hasLastName: !!(user && user.profile && user.profile.last_name),
          userKeys: user ? Object.keys(user) : [],
        });

        // Token already stored by startTokenRefresh, but ensure it's set
        tokenManager.setTokens(accessToken);
        setImpersonationFlag();

        // Mark initialization as complete so other requests can proceed
        markInitializationComplete();

        yield put({
          type: 'FETCH_CREDENTIALS_FULFILLED',
          payload: { data: user },
        });

        refreshSucceeded = true;
      } else {
        // No valid session - but DON'T set refreshSucceeded to true
        // Let the finally block handle dispatching the action
        console.log('[App Init] No valid session from refresh token');
        markInitializationComplete();
        setImpersonationFlag();
      }
    } catch (error) {
      // Not authenticated or refresh failed - continue as guest
      console.log('[App Init] User not authenticated on app init (no refresh token or refresh failed)', error);
      console.log('[App Init] Error details:', {
        message: error?.message,
        response: error?.response?.status,
        statusText: error?.response?.statusText,
      });

      // Always mark initialization complete so app can proceed
      markInitializationComplete();
      setImpersonationFlag();

      // Don't set refreshSucceeded - let finally block handle the action dispatch
    } finally {
      // CRITICAL: Always ensure loginPending is set to false if refresh didn't succeed
      // This ensures the login form is enabled even if something goes wrong
      if (!refreshSucceeded) {
        console.log('[App Init] Refresh did not succeed, dispatching FETCH_CREDENTIALS_FULFILLED with empty data');
        yield put({
          type: 'FETCH_CREDENTIALS_FULFILLED',
          payload: { data: '' },
        });
        console.log('[App Init] Successfully dispatched FETCH_CREDENTIALS_FULFILLED with empty data to enable login form');
      }
    }
  }
}

export function* initWebApp() {
  // Continuous watcher - waits for APP_INIT indefinitely
  while (true) {
    yield take('APP_INIT');

    // Prevent multiple initializations
    if (hasInitialized) {
      console.log('[App Init] Already initialized, ignoring subsequent APP_INIT');
      continue; // Wait for next APP_INIT but don't process it
    }
    hasInitialized = true;

    // Perform the actual initialization
    yield call(performInitialization);
  }
}
